import React, { useState, useRef, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import BASE_URL from '../config';
import {
  FaReceipt,
  FaPlus,
  FaCamera,
  FaUpload,
  FaTrash,
  FaEdit,
  FaTimes,
  FaCheck,
  FaImage,
  FaSync,
  FaDownload,
  FaDollarSign,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaUser,
  FaFilePdf,
  FaFileCsv,
  FaSearchPlus,
  FaSearchMinus,
  FaUndo,
  FaLock,
  FaLockOpen
} from 'react-icons/fa';
import { getExpenses, createExpense, updateExpense, deleteExpense, getExpenseReceipt, analyzeReceipt, getMembers, lockExpenses, unlockExpenses } from '../api/client';
import ConfirmDialog from './ConfirmDialog';
import PageGuide from './PageGuide';
import './Expenses.css';

const CURRENCY_OPTIONS = [
  { value: 'USD', label: '$ USD', symbol: '$' },
  { value: 'EUR', label: '\u20AC EUR', symbol: '\u20AC' },
  { value: 'GBP', label: '\u00A3 GBP', symbol: '\u00A3' },
  { value: 'JPY', label: '\u00A5 JPY', symbol: '\u00A5' },
  { value: 'CAD', label: 'C$ CAD', symbol: 'C$' },
  { value: 'AUD', label: 'A$ AUD', symbol: 'A$' },
  { value: 'INR', label: '\u20B9 INR', symbol: '\u20B9' },
  { value: 'CNY', label: '\u00A5 CNY', symbol: '\u00A5' },
  { value: 'MXN', label: '$ MXN', symbol: '$' },
  { value: 'BRL', label: 'R$ BRL', symbol: 'R$' },
];

const PAID_TIERS = ['individual', 'group', 'starter_agent', 'professional_agent', 'enterprise_agency', 'promotional', 'unlimited', 'admin'];
const FREE_EXPENSE_LIMIT = 3;

const Expenses = ({ requestId, isOwner, onExpenseChange, userTier, limitsWaived = false, onViewPricing }) => {
  // limitsWaived: non-paid user on a short trip (<= 3 days) gets full access.
  const isFreeUser = !limitsWaived && !PAID_TIERS.includes(userTier || 'free');
  const [expenses, setExpenses] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [primaryCurrency, setPrimaryCurrency] = useState('USD');
  const [settlements, setSettlements] = useState([]);
  const [tripEnded, setTripEnded] = useState(false);
  const [expensesLocked, setExpensesLocked] = useState(false);
  const [expensesLockedAt, setExpensesLockedAt] = useState(null);
  const [isLocking, setIsLocking] = useState(false);
  const [lockConfirm, setLockConfirm] = useState({ isOpen: false, action: null });
  const [members, setMembers] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentUserName, setCurrentUserName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [viewingReceipt, setViewingReceipt] = useState(null);
  const [receiptBlobUrl, setReceiptBlobUrl] = useState(null);
  const [imageZoom, setImageZoom] = useState(1);

  // Form state
  const [formData, setFormData] = useState({
    expense_date: new Date().toISOString().slice(0, 16),
    place_name: '',
    amount: '',
    currency: 'USD',
    description: '',
    paid_by_user_id: null,
  });
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraError, setCameraError] = useState(null);

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, expenseId: null });

  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const isMountedRef = useRef(true);
  const receiptContainerRef = useRef(null);
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });

  // Cleanup on unmount to prevent state updates after navigation
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchExpenses = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getExpenses(requestId);
      // Only update state if component is still mounted
      if (!isMountedRef.current) return;
      setExpenses(data.expenses || []);
      setTotalAmount(data.total_amount || 0);
      setPrimaryCurrency(data.currency || 'USD');
      setSettlements(data.settlements || []);
      setTripEnded(!!data.trip_ended);
      setExpensesLocked(!!data.expenses_locked);
      setExpensesLockedAt(data.expenses_locked_at || null);
    } catch (err) {
      if (!isMountedRef.current) return;
      console.error('Error fetching expenses:', err);
      setError('Failed to load expenses');
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [requestId]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  // Fetch members and current user on mount
  useEffect(() => {
    const loadMembersAndUser = async () => {
      try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          setCurrentUserId(user.id);
          setCurrentUserName(user.full_name || user.email || '');
        }
      } catch (e) { /* ignore */ }
      try {
        const data = await getMembers(requestId);
        if (isMountedRef.current) {
          setMembers(data.members || []);
        }
      } catch (e) { /* not a group trip or no members */ }
    };
    loadMembersAndUser();
  }, [requestId]);

  // All trip members for "Paid By" selector
  // Ensure the current user is always included as a safety net
  const activeMembersList = members
    .filter(m => m.status === 'active' && m.user_id)
    .map(m => ({ id: m.user_id, name: m.full_name || m.email }));
  const allTripMembers = currentUserId
    ? [
        ...(!activeMembersList.some(m => m.id === currentUserId)
          ? [{ id: currentUserId, name: currentUserName || 'Me' }]
          : []),
        ...activeMembersList,
      ]
    : activeMembersList;
  const isGroupTrip = allTripMembers.length > 1;

  const resetForm = () => {
    setFormData({
      expense_date: new Date().toISOString().slice(0, 16),
      place_name: '',
      amount: '',
      currency: 'USD',
      description: '',
      paid_by_user_id: currentUserId,
    });
    setReceiptFile(null);
    setReceiptPreview(null);
    setEditingExpense(null);
    setShowAddForm(false);
    stopCamera();
  };

  // Camera functions
  const startCamera = async () => {
    setCameraError(null);
    setShowCamera(true);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error('Error accessing camera:', err);
      setCameraError('Unable to access camera. Please check permissions or use file upload instead.');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setShowCamera(false);
    setCameraError(null);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw the video frame to canvas
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert canvas to blob and create file
    canvas.toBlob(async (blob) => {
      if (blob) {
        const file = new File([blob], `receipt_${Date.now()}.jpg`, { type: 'image/jpeg' });
        stopCamera();
        processReceiptFile(file);
      }
    }, 'image/jpeg', 0.9);
  };

  const processReceiptFile = async (file) => {
    if (!file) return;

    setReceiptFile(file);

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setReceiptPreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setReceiptPreview(null);
    }

    // Analyze receipt with AI
    setIsAnalyzing(true);
    setError(null);
    try {
      const extractedData = await analyzeReceipt(file, requestId);

      // Auto-fill form with extracted data
      setFormData(prev => ({
        ...prev,
        place_name: extractedData.place_name || prev.place_name,
        amount: extractedData.amount ? extractedData.amount.toString() : prev.amount,
        currency: extractedData.currency || prev.currency,
        description: extractedData.description || prev.description,
        expense_date: extractedData.expense_date
          ? new Date(extractedData.expense_date).toISOString().slice(0, 16)
          : prev.expense_date
      }));
    } catch (err) {
      console.error('Error analyzing receipt:', err);
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 403) {
        setError(detail || 'Receipt scanning requires a Group plan or higher. Upgrade to unlock this feature.');
      } else if (status === 400) {
        setError(detail || 'Unable to process this file. Please try a different image or PDF.');
      } else if (status === 413) {
        setError('Receipt file is too large. Please use a smaller image.');
      } else {
        setError('Receipt scan failed. You can still fill in the details manually below.');
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    processReceiptFile(file);
  };

  const hasReachedExpenseLimit = isFreeUser && expenses.length >= FREE_EXPENSE_LIMIT;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.place_name || !formData.amount) {
      setError('Please fill in required fields');
      return;
    }

    if (isFreeUser && !editingExpense && expenses.length >= FREE_EXPENSE_LIMIT) {
      setError('Free plan expense limit reached. Upgrade for unlimited expenses.');
      if (onViewPricing) onViewPricing();
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const expenseData = {
        expense_date: new Date(formData.expense_date).toISOString(),
        place_name: formData.place_name,
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        description: formData.description || null,
        paid_by_user_id: formData.paid_by_user_id || currentUserId,
      };

      if (editingExpense) {
        await updateExpense(requestId, editingExpense.id, expenseData, receiptFile);
      } else {
        await createExpense(requestId, expenseData, receiptFile);
      }

      await fetchExpenses();
      resetForm();
      // Notify parent to refresh itinerary totals
      if (onExpenseChange) {
        onExpenseChange();
      }
    } catch (err) {
      console.error('Error saving expense:', err);
      setError(err.response?.data?.detail || 'Failed to save expense');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setFormData({
      expense_date: new Date(expense.expense_date).toISOString().slice(0, 16),
      place_name: expense.place_name,
      amount: expense.amount.toString(),
      currency: expense.currency,
      description: expense.description || '',
      paid_by_user_id: expense.paid_by_user_id || currentUserId,
    });
    setReceiptFile(null);
    setReceiptPreview(null);
    setShowAddForm(true);
  };

  const handleDelete = (expenseId) => {
    setConfirmDialog({ isOpen: true, expenseId });
  };

  const confirmDeleteExpense = async () => {
    const expenseId = confirmDialog.expenseId;
    setConfirmDialog({ isOpen: false, expenseId: null });

    try {
      await deleteExpense(requestId, expenseId);
      await fetchExpenses();
      // Notify parent to refresh itinerary totals
      if (onExpenseChange) {
        onExpenseChange();
      }
    } catch (err) {
      console.error('Error deleting expense:', err);
      setError(err.response?.data?.detail || 'Failed to delete expense');
    }
  };

  const confirmLockAction = async () => {
    const action = lockConfirm.action;
    setLockConfirm({ isOpen: false, action: null });
    setIsLocking(true);
    setError(null);
    try {
      if (action === 'lock') {
        await lockExpenses(requestId);
      } else {
        await unlockExpenses(requestId);
      }
      await fetchExpenses();
    } catch (err) {
      console.error('Error changing expense lock:', err);
      setError(err.response?.data?.detail || `Failed to ${action} expenses`);
    } finally {
      if (isMountedRef.current) setIsLocking(false);
    }
  };

  const handleViewReceipt = async (expense) => {
    if (!expense.has_receipt) return;

    try {
      const blob = await getExpenseReceipt(requestId, expense.id);
      const url = URL.createObjectURL(blob);
      setReceiptBlobUrl(url);
      setViewingReceipt(expense);
    } catch (err) {
      console.error('Error loading receipt:', err);
      setError('Failed to load receipt');
    }
  };

  const closeReceiptViewer = () => {
    if (receiptBlobUrl) {
      URL.revokeObjectURL(receiptBlobUrl);
    }
    setReceiptBlobUrl(null);
    setViewingReceipt(null);
    setImageZoom(1);
  };

  // Receipt image pan/drag handlers
  const handleReceiptMouseDown = useCallback((e) => {
    const container = receiptContainerRef.current;
    if (!container || imageZoom <= 1) return;
    isDraggingRef.current = true;
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      scrollLeft: container.scrollLeft,
      scrollTop: container.scrollTop,
    };
    e.preventDefault();
  }, [imageZoom]);

  const handleReceiptMouseMove = useCallback((e) => {
    if (!isDraggingRef.current) return;
    const container = receiptContainerRef.current;
    if (!container) return;
    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
    container.scrollLeft = dragStartRef.current.scrollLeft - dx;
    container.scrollTop = dragStartRef.current.scrollTop - dy;
  }, []);

  const handleReceiptMouseUp = useCallback(() => {
    isDraggingRef.current = false;
  }, []);

  const handleReceiptWheel = useCallback((e) => {
    e.preventDefault();
    setImageZoom(prev => {
      const delta = e.deltaY > 0 ? -0.25 : 0.25;
      return Math.min(3, Math.max(0.5, prev + delta));
    });
  }, []);

  const downloadReceipt = () => {
    if (receiptBlobUrl && viewingReceipt) {
      const a = document.createElement('a');
      a.href = receiptBlobUrl;
      a.download = viewingReceipt.receipt_filename || 'receipt';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount, currency) => {
    const currencyInfo = CURRENCY_OPTIONS.find(c => c.value === currency) || { symbol: '$' };
    return `${currencyInfo.symbol}${parseFloat(amount).toFixed(2)}`;
  };

  const getCurrencySymbol = (currency) => {
    const currencyInfo = CURRENCY_OPTIONS.find(c => c.value === currency);
    return currencyInfo ? currencyInfo.symbol : '$';
  };

  // Export expenses to CSV
  const exportToCSV = () => {
    if (expenses.length === 0) {
      setError('No expenses to export');
      return;
    }

    const headers = ['Date', 'Place', 'Amount', 'Currency', 'Description', 'Added By', 'Paid By'];
    const csvRows = [headers.join(',')];

    expenses.forEach(expense => {
      const row = [
        new Date(expense.expense_date).toLocaleDateString(),
        `"${expense.place_name.replace(/"/g, '""')}"`,
        expense.amount.toFixed(2),
        expense.currency,
        `"${(expense.description || '').replace(/"/g, '""')}"`,
        `"${expense.user_name}${expense.member_code ? ' #' + expense.member_code : ''}"`,
        `"${expense.paid_by_user_name || expense.user_name}"`
      ];
      csvRows.push(row.join(','));
    });

    // Add total row
    csvRows.push('');
    csvRows.push(`"Total",,"${totalAmount.toFixed(2)}","${primaryCurrency}","",""`)

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `trip-expenses-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Export expenses to PDF
  const exportToPDF = () => {
    if (expenses.length === 0) {
      setError('No expenses to export');
      return;
    }

    // Create a printable HTML document
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      setError('Please allow popups to export PDF');
      return;
    }

    const currencySymbol = getCurrencySymbol(primaryCurrency);

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Trip Expenses Report</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            padding: 40px;
            color: #333;
            line-height: 1.5;
          }
          .header {
            text-align: center;
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 2px solid #877B98;
          }
          .header h1 {
            color: #877B98;
            font-size: 28px;
            margin-bottom: 5px;
          }
          .header .date {
            color: #666;
            font-size: 14px;
          }
          .summary {
            background: #f8f6f4;
            padding: 20px;
            border-radius: 8px;
            margin-bottom: 30px;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          .summary .total-label {
            color: #666;
            font-size: 14px;
          }
          .summary .total-amount {
            font-size: 24px;
            font-weight: bold;
            color: #877B98;
          }
          .summary .expense-count {
            color: #666;
            font-size: 14px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 20px;
          }
          th {
            background: #877B98;
            color: white;
            padding: 12px 15px;
            text-align: left;
            font-weight: 600;
          }
          th:last-child {
            text-align: right;
          }
          td {
            padding: 12px 15px;
            border-bottom: 1px solid #e5e0da;
          }
          td:last-child {
            text-align: right;
            font-weight: 600;
          }
          tr:nth-child(even) {
            background: #faf9f8;
          }
          .place-name {
            font-weight: 500;
          }
          .expense-meta {
            font-size: 12px;
            color: #888;
            margin-top: 2px;
          }
          .description {
            font-size: 13px;
            color: #666;
            font-style: italic;
          }
          .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #e5e0da;
            text-align: center;
            color: #888;
            font-size: 12px;
          }
          .total-row {
            background: #f0eef2 !important;
            font-weight: bold;
          }
          .total-row td {
            border-bottom: none;
            padding-top: 15px;
            padding-bottom: 15px;
          }
          @media print {
            body { padding: 20px; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Trip Expenses Report</h1>
          <div class="date">Generated on ${new Date().toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}</div>
        </div>

        <div class="summary">
          <div>
            <div class="total-label">Total Expenses</div>
            <div class="total-amount">${currencySymbol}${totalAmount.toFixed(2)} ${primaryCurrency}</div>
          </div>
          <div class="expense-count">${expenses.length} expense${expenses.length !== 1 ? 's' : ''}</div>
        </div>

        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Details</th>
              <th>Added By</th>
              <th>Paid By</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            ${expenses.map(expense => `
              <tr>
                <td>${new Date(expense.expense_date).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })}</td>
                <td>
                  <div class="place-name">${expense.place_name}</div>
                  ${expense.description ? `<div class="description">${expense.description}</div>` : ''}
                </td>
                <td>
                  ${expense.user_name}
                  ${expense.member_code ? `<span class="expense-meta">#${expense.member_code}</span>` : ''}
                </td>
                <td>${expense.paid_by_user_name || expense.user_name}</td>
                <td>${getCurrencySymbol(expense.currency)}${expense.amount.toFixed(2)} ${expense.currency}</td>
              </tr>
            `).join('')}
            <tr class="total-row">
              <td colspan="4"><strong>Total</strong></td>
              <td><strong>${currencySymbol}${totalAmount.toFixed(2)} ${primaryCurrency}</strong></td>
            </tr>
          </tbody>
        </table>

        ${settlements && settlements.length > 0 ? `
        <div style="margin-top: 30px;">
          <h2 style="color: #877B98; font-size: 20px; margin-bottom: 15px;">Settlement Summary</h2>
          <div style="background: #fefce8; padding: 15px; border-radius: 8px; border: 1px solid #fef08a;">
            ${settlements.map(s => `
              <p style="margin: 8px 0; font-size: 14px; color: #374151;">
                <strong>${s.from_user_name}</strong> owes
                <strong>${getCurrencySymbol(s.currency)}${s.amount.toFixed(2)} ${s.currency}</strong> to
                <strong>${s.to_user_name}</strong>
              </p>
            `).join('')}
          </div>
        </div>
        ` : ''}

        <div class="footer">
          <p>Generated by ItinerarySeek - Your AI Travel Companion</p>
          <p>${BASE_URL}</p>
        </div>

        <script>
          window.onload = function() {
            window.print();
          }
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(DOMPurify.sanitize(htmlContent, { WHOLE_DOCUMENT: true }));
    printWindow.document.close();
  };

  return (
    <div className="expenses-container">
      {/* Header */}
      <div className="expenses-header">
        <div className="expenses-header-left">
          <h2><FaReceipt /> Trip Expenses</h2>
          <span className="expenses-count">{expenses.length} expense{expenses.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="expenses-header-right">
          <div className="expenses-total">
            <span className="total-label">Total:</span>
            <span className="total-amount">{getCurrencySymbol(primaryCurrency)}{totalAmount.toFixed(2)} {primaryCurrency}</span>
          </div>
          <div className="expenses-export-btns">
            <button
              className="expenses-export-btn"
              onClick={exportToCSV}
              disabled={expenses.length === 0}
              title="Export as CSV"
            >
              <FaFileCsv /> CSV
            </button>
            <button
              className="expenses-export-btn"
              onClick={exportToPDF}
              disabled={expenses.length === 0}
              title="Export as PDF"
            >
              <FaFilePdf /> PDF
            </button>
          </div>
          <button
            className="expenses-refresh-btn"
            onClick={fetchExpenses}
            disabled={isLoading}
            title="Refresh expenses"
          >
            <FaSync className={isLoading ? 'spinning' : ''} />
          </button>
          <button
            className="expenses-add-btn"
            onClick={() => {
              if (hasReachedExpenseLimit) {
                if (onViewPricing) onViewPricing();
                return;
              }
              setShowAddForm(true);
            }}
            disabled={showAddForm || expensesLocked}
            title={expensesLocked ? 'Expenses are locked' : 'Add Expense'}
          >
            <FaPlus /> Add Expense
          </button>
          <PageGuide pageKey="expenses" />
        </div>
      </div>

      {/* Free plan limit banner */}
      {isFreeUser && (
        <div className={`flex items-center justify-between p-3 rounded-lg border mb-3 ${hasReachedExpenseLimit ? 'bg-amber-50 border-amber-200' : 'bg-sky-50 border-sky-200'}`}>
          <p className={`text-sm font-medium ${hasReachedExpenseLimit ? 'text-amber-700' : 'text-sky-700'}`}>
            {hasReachedExpenseLimit
              ? 'You\'ve reached the free plan limit for expenses.'
              : `Free plan: ${expenses.length}/${FREE_EXPENSE_LIMIT} expenses added`}
          </p>
          {hasReachedExpenseLimit && onViewPricing && (
            <button
              onClick={onViewPricing}
              className="px-3 py-1.5 text-xs font-semibold bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors"
            >
              Upgrade Plan
            </button>
          )}
        </div>
      )}

      {/* Expense lock controls */}
      {expensesLocked ? (
        <div className="expenses-lock-banner locked">
          <div className="lock-banner-text">
            <FaLock />
            <span>
              Expenses are locked{expensesLockedAt ? ` (${formatDate(expensesLockedAt)})` : ''}. No further changes can be made.
            </span>
          </div>
          {isOwner && (
            <button
              className="expenses-unlock-btn"
              onClick={() => setLockConfirm({ isOpen: true, action: 'unlock' })}
              disabled={isLocking}
            >
              {isLocking ? <><FaSync className="spinning" /> Unlocking...</> : <><FaLockOpen /> Unlock Expenses</>}
            </button>
          )}
        </div>
      ) : isOwner && (
        tripEnded ? (
          <div className="expenses-lock-banner">
            <div className="lock-banner-text">
              <FaLock />
              <span>The trip is over. Lock expenses to freeze the ledger for settlement — no one will be able to add, edit, or delete expenses.</span>
            </div>
            <button
              className="expenses-lock-btn"
              onClick={() => setLockConfirm({ isOpen: true, action: 'lock' })}
              disabled={isLocking}
            >
              {isLocking ? <><FaSync className="spinning" /> Locking...</> : <><FaLock /> Lock Expenses</>}
            </button>
          </div>
        ) : (
          <div className="expenses-lock-banner info">
            <div className="lock-banner-text">
              <FaLock />
              <span>You'll be able to lock expenses once the trip is over. Locking freezes the ledger so expenses can no longer be changed.</span>
            </div>
          </div>
        )
      )}

      {/* Error Message */}
      {error && (
        <div className="expenses-error">
          {error}
          <button onClick={() => setError(null)}><FaTimes /></button>
        </div>
      )}

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="expenses-form-container">
          <div className="expenses-form-header">
            <h3>{editingExpense ? 'Edit Expense' : 'Add New Expense'}</h3>
            <button className="form-close-btn" onClick={resetForm}>
              <FaTimes />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="expenses-form">
            {/* Receipt Upload - First to enable auto-fill */}
            <div className="form-group receipt-group receipt-first">
              <label><FaReceipt /> Scan Receipt (auto-fills details)</label>
              <div className="receipt-actions">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept="image/*,.pdf"
                  style={{ display: 'none' }}
                />
                <button
                  type="button"
                  className="receipt-btn receipt-btn-primary"
                  onClick={startCamera}
                  disabled={isAnalyzing}
                >
                  <FaCamera /> Take Photo
                </button>
                <button
                  type="button"
                  className="receipt-btn receipt-btn-primary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isAnalyzing}
                >
                  <FaUpload /> Upload File
                </button>
              </div>
              {isAnalyzing && (
                <div className="analyzing-indicator">
                  <FaSync className="spinning" /> Analyzing receipt...
                </div>
              )}
              {receiptFile && !isAnalyzing && (
                <div className="receipt-preview-container">
                  {receiptPreview ? (
                    <img src={receiptPreview} alt="Receipt preview" className="receipt-preview" />
                  ) : (
                    <div className="receipt-file-info">
                      <FaReceipt />
                      <span>{receiptFile.name}</span>
                    </div>
                  )}
                  <button
                    type="button"
                    className="remove-receipt-btn"
                    onClick={() => {
                      setReceiptFile(null);
                      setReceiptPreview(null);
                    }}
                  >
                    <FaTimes />
                  </button>
                </div>
              )}
              {editingExpense?.has_receipt && !receiptFile && (
                <div className="existing-receipt-note">
                  <FaImage /> Current receipt attached. Upload a new one to replace.
                </div>
              )}
            </div>

            <div className="form-divider">
              <span>Expense Details</span>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label><FaCalendarAlt /> Date & Time *</label>
                <input
                  type="datetime-local"
                  value={formData.expense_date}
                  onChange={(e) => setFormData({ ...formData, expense_date: e.target.value })}
                  required
                  disabled={isAnalyzing}
                />
              </div>
              <div className="form-group">
                <label><FaMapMarkerAlt /> Place Name *</label>
                <input
                  type="text"
                  placeholder="Restaurant, Hotel, etc."
                  value={formData.place_name}
                  onChange={(e) => setFormData({ ...formData, place_name: e.target.value })}
                  required
                  maxLength={255}
                  disabled={isAnalyzing}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group amount-group">
                <label><FaDollarSign /> Amount *</label>
                <div className="amount-input-wrapper">
                  <select
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    className="currency-select"
                    disabled={isAnalyzing}
                  >
                    {CURRENCY_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <input
                    type="number"
                    placeholder="0.00"
                    step="0.01"
                    min="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    required
                    disabled={isAnalyzing}
                  />
                </div>
              </div>
              {/* Paid By - only for group trips */}
              {isGroupTrip && (
                <div className="form-group">
                  <label><FaUser /> Paid By</label>
                  <select
                    value={formData.paid_by_user_id || ''}
                    onChange={(e) => setFormData({ ...formData, paid_by_user_id: parseInt(e.target.value) || null })}
                    className="paid-by-select"
                    disabled={isAnalyzing}
                  >
                    <option value="">Select who paid...</option>
                    {allTripMembers.map(m => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Description (optional)</label>
              <textarea
                placeholder="Brief description of the expense..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
                disabled={isAnalyzing}
              />
            </div>

            <div className="form-actions">
              <button type="button" className="cancel-btn" onClick={resetForm}>
                Cancel
              </button>
              <button type="submit" className="submit-btn" disabled={isSubmitting}>
                {isSubmitting ? (
                  <><FaSync className="spinning" /> Saving...</>
                ) : (
                  <><FaCheck /> {editingExpense ? 'Update' : 'Add'} Expense</>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Expenses List */}
      <div className="expenses-list">
        {isLoading && expenses.length === 0 ? (
          <div className="expenses-loading">
            <FaSync className="spinning" />
            <span>Loading expenses...</span>
          </div>
        ) : expenses.length === 0 ? (
          <div className="expenses-empty">
            <FaReceipt size={48} />
            <h3>No expenses yet</h3>
            <p>Track your trip expenses by adding receipts</p>
            {!expensesLocked && (
              <button className="add-first-btn" onClick={() => setShowAddForm(true)}>
                <FaPlus /> Add First Expense
              </button>
            )}
          </div>
        ) : (
          expenses.map((expense) => (
            <div key={expense.id} className="expense-card">
              <div className="expense-main">
                <div className="expense-icon">
                  {expense.has_receipt ? <FaImage /> : <FaReceipt />}
                </div>
                <div className="expense-details">
                  <div className="expense-place">{expense.place_name}</div>
                  <div className="expense-meta">
                    <span className="expense-date">
                      <FaCalendarAlt /> {formatDate(expense.expense_date)}
                    </span>
                    <span className="expense-user">
                      <FaUser /> {expense.user_name}
                      {expense.member_code && <span className="member-code">#{expense.member_code}</span>}
                    </span>
                    {expense.paid_by_user_name && expense.paid_by_user_id !== expense.user_id && (
                      <span className="expense-paid-by">
                        <FaDollarSign /> Paid by {expense.paid_by_user_name}
                      </span>
                    )}
                  </div>
                  {expense.description && (
                    <div className="expense-description">{expense.description}</div>
                  )}
                </div>
                <div className="expense-amount">
                  {formatAmount(expense.amount, expense.currency)}
                </div>
              </div>
              <div className="expense-actions">
                {expense.has_receipt && (
                  <button
                    className="expense-action-btn view-receipt-btn"
                    onClick={() => handleViewReceipt(expense)}
                    title="View receipt"
                  >
                    <FaImage /> View Receipt
                  </button>
                )}
                {!expensesLocked && (
                  <>
                    <button
                      className="expense-action-btn edit-btn"
                      onClick={() => handleEdit(expense)}
                      title="Edit expense"
                    >
                      <FaEdit />
                    </button>
                    <button
                      className="expense-action-btn delete-btn"
                      onClick={() => handleDelete(expense.id)}
                      title="Delete expense"
                    >
                      <FaTrash />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Expense Breakdown - Who Paid What */}
      {expenses.length > 0 && (() => {
        // Group expenses by payer and currency
        const payerTotals = {};
        expenses.forEach(expense => {
          const payerName = expense.paid_by_user_name || expense.user_name;
          const payerId = expense.paid_by_user_id || expense.user_id;
          const key = `${payerId}-${expense.currency}`;
          if (!payerTotals[key]) {
            payerTotals[key] = { name: payerName, amount: 0, currency: expense.currency };
          }
          payerTotals[key].amount += parseFloat(expense.amount);
        });
        const breakdownEntries = Object.values(payerTotals).sort((a, b) => b.amount - a.amount);
        return (
          <div className="expense-breakdown">
            <h3 className="breakdown-title"><FaDollarSign /> Expense Breakdown</h3>
            <div className="breakdown-list">
              {breakdownEntries.map((entry, idx) => (
                <div key={idx} className="breakdown-item">
                  <div className="breakdown-payer">
                    <FaUser className="breakdown-icon" />
                    <span className="breakdown-name">{entry.name}</span>
                  </div>
                  <div className="breakdown-amount-bar">
                    <div
                      className="breakdown-bar-fill"
                      style={{
                        width: `${Math.min(100, (entry.amount / Math.max(...breakdownEntries.map(e => e.amount))) * 100)}%`
                      }}
                    />
                  </div>
                  <span className="breakdown-amount-value">{formatAmount(entry.amount, entry.currency)}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Settlement Summary */}
      {settlements && settlements.length > 0 && (
        <div className="settlement-summary">
          <h3 className="settlement-title"><FaDollarSign /> Settlement Summary</h3>
          <div className="settlement-list">
            {settlements.map((s, idx) => (
              <div key={idx} className="settlement-item">
                <span className="settlement-from">{s.from_user_name}</span>
                <span className="settlement-arrow"> owes </span>
                <span className="settlement-amount">{formatAmount(s.amount, s.currency)}</span>
                <span className="settlement-arrow"> to </span>
                <span className="settlement-to">{s.to_user_name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Receipt Viewer Modal */}
      {viewingReceipt && (
        <div className="receipt-modal-overlay" onClick={closeReceiptViewer}>
          <div className="receipt-modal" onClick={(e) => e.stopPropagation()}>
            <div className="receipt-modal-header">
              <h3><FaReceipt /> Receipt - {viewingReceipt.place_name}</h3>
              <div className="receipt-modal-actions">
                {/* Zoom controls for images only */}
                {receiptBlobUrl && !viewingReceipt.receipt_filename?.toLowerCase().endsWith('.pdf') && (
                  <div className="zoom-controls">
                    <button
                      className="zoom-btn"
                      onClick={() => setImageZoom(prev => Math.max(0.5, prev - 0.25))}
                      disabled={imageZoom <= 0.5}
                      title="Zoom Out"
                    >
                      <FaSearchMinus />
                    </button>
                    <span className="zoom-level">{Math.round(imageZoom * 100)}%</span>
                    <button
                      className="zoom-btn"
                      onClick={() => setImageZoom(prev => Math.min(3, prev + 0.25))}
                      disabled={imageZoom >= 3}
                      title="Zoom In"
                    >
                      <FaSearchPlus />
                    </button>
                    <button
                      className="zoom-btn"
                      onClick={() => setImageZoom(1)}
                      disabled={imageZoom === 1}
                      title="Reset Zoom"
                    >
                      <FaUndo />
                    </button>
                  </div>
                )}
                <button className="download-btn" onClick={downloadReceipt}>
                  <FaDownload /> Download
                </button>
                <button className="close-modal-btn" onClick={closeReceiptViewer}>
                  <FaTimes />
                </button>
              </div>
            </div>
            <div className="receipt-modal-content">
              {receiptBlobUrl && (
                viewingReceipt.receipt_filename?.toLowerCase().endsWith('.pdf') ? (
                  <iframe
                    src={receiptBlobUrl}
                    title="Receipt PDF"
                    className="receipt-pdf-viewer"
                  />
                ) : (
                  <div
                    className="receipt-image-container"
                    ref={receiptContainerRef}
                    onMouseDown={handleReceiptMouseDown}
                    onMouseMove={handleReceiptMouseMove}
                    onMouseUp={handleReceiptMouseUp}
                    onMouseLeave={handleReceiptMouseUp}
                    onWheel={handleReceiptWheel}
                    style={{ cursor: imageZoom > 1 ? (isDraggingRef.current ? 'grabbing' : 'grab') : 'default' }}
                  >
                    <img
                      src={receiptBlobUrl}
                      alt="Receipt"
                      className="receipt-image-viewer"
                      draggable={false}
                      style={{
                        width: imageZoom !== 1 ? `${imageZoom * 100}%` : undefined,
                        maxWidth: imageZoom > 1 ? 'none' : '100%',
                        maxHeight: imageZoom > 1 ? 'none' : '70vh',
                      }}
                    />
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      )}

      {/* Camera Modal */}
      {showCamera && (
        <div className="camera-modal-overlay">
          <div className="camera-modal">
            <div className="camera-modal-header">
              <h3><FaCamera /> Take Photo of Receipt</h3>
              <button className="close-modal-btn" onClick={stopCamera}>
                <FaTimes />
              </button>
            </div>
            <div className="camera-modal-content">
              {cameraError ? (
                <div className="camera-error">
                  <p>{cameraError}</p>
                  <button className="cancel-btn" onClick={stopCamera}>
                    Close
                  </button>
                </div>
              ) : (
                <>
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    className="camera-video"
                  />
                  <canvas ref={canvasRef} style={{ display: 'none' }} />
                  <div className="camera-controls">
                    <button className="cancel-btn" onClick={stopCamera}>
                      Cancel
                    </button>
                    <button className="capture-btn" onClick={capturePhoto}>
                      <FaCamera /> Capture
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, expenseId: null })}
        onConfirm={confirmDeleteExpense}
        title="Delete Expense"
        message="Are you sure you want to delete this expense? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Lock / Unlock Confirm Dialog */}
      <ConfirmDialog
        isOpen={lockConfirm.isOpen}
        onClose={() => setLockConfirm({ isOpen: false, action: null })}
        onConfirm={confirmLockAction}
        title={lockConfirm.action === 'unlock' ? 'Unlock Expenses' : 'Lock Expenses'}
        message={
          lockConfirm.action === 'unlock'
            ? 'Unlock expenses so they can be added, edited, and deleted again?'
            : 'Lock all expenses for this trip? Once locked, no one (including you) can add, edit, or delete expenses until you unlock them.'
        }
        confirmText={lockConfirm.action === 'unlock' ? 'Unlock' : 'Lock'}
        cancelText="Cancel"
        type={lockConfirm.action === 'unlock' ? 'warning' : 'danger'}
      />
    </div>
  );
};

export default Expenses;
